import { FC, useState, ReactNode, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { ReactComponent as Check } from "assets/images/iconsV2/check.svg";
import "./ChoiceChip.scss";

interface BaseProps {
  /**
   * Indicates if multiple selections are allowed.
   */
  multiple?: boolean;
  /**
   * The available options for the choice chips, an array of objects with label and value.
   */
  options: { label: string; value: string | number; icon?: ReactNode; disabled?: boolean }[];
}

interface WithReactHookFormProps extends BaseProps {
  /**
   * The name attribute for the choice chip, used as a key for form data.
   */
  name: string;
  /**
   * RegisterOptions object for react-hook-form.
   */
  registerOptions?: object;
}

interface WithoutReactHookFormProps extends BaseProps {
  /**
   * The name prop is set to never, indicating it should not be used when not using react-hook-form.
   */
  name?: never;
  /**
   * Callback for change events when not using react-hook-form.
   */
  onChange: (value: string | number | (string | number)[]) => void;
  /**
   * Controlled selected value or values.
   */
  selected?: string | number | (string | number)[];
}

export type ChoiceChipProps = WithReactHookFormProps | WithoutReactHookFormProps;

const ChoiceChip: FC<ChoiceChipProps> = function (props) {
  const { options, multiple = false } = props;

  // Check if using React Hook Form
  const formContext = useFormContext();
  const isUsingReactHookForm = typeof props.name === "string" && !!formContext?.register;

  // Initialize internal selected state based on whether multiple selections are allowed
  const [internalSelected, setInternalSelected] = useState<string | number | (string | number)[]>(multiple ? [] : "");

  useEffect(() => {
    if (!isUsingReactHookForm && "selected" in props) {
      setInternalSelected(props.selected || []);
    }
  }, []);

  const handleSelect = (value: string | number) => {
    if (multiple) {
      const selectedArray = internalSelected as (string | number)[];
      if (!Array.isArray(selectedArray)) return; // Safety check

      const newSelection = selectedArray.includes(value)
        ? selectedArray.filter((v) => v !== value)
        : [...selectedArray, value];

      setInternalSelected(newSelection); // Update internal state
      (props as WithoutReactHookFormProps).onChange(newSelection); // Call custom onChange
    } else {
      setInternalSelected(value);
      (props as WithoutReactHookFormProps).onChange(value); // Call custom onChange
    }
  };

  const handleChangeHookForm = (value: string | number) => {
    const currentValue = formContext?.getValues(props.name!) || [];
    if (multiple) {
      const newValue = currentValue.includes(value)
        ? currentValue.filter((v: string | number) => v !== value)
        : [...currentValue, value];
      formContext?.setValue(props.name!, newValue);
    } else {
      formContext?.setValue(props.name!, value);
    }
  };

  return (
    <div className="choice-chip-container">
      {options.map((option) => {
        let isSelected = false;

        if (multiple) {
          if (isUsingReactHookForm) isSelected = formContext?.getValues(props.name!)?.includes(option.value);
          else isSelected = (internalSelected as (string | number)[]).includes(option.value);
        } else if (isUsingReactHookForm) isSelected = formContext?.getValues(props.name!) === option.value;
        else isSelected = internalSelected === option.value;

        const selectionClass = isSelected ? "selected" : "";
        const modeClass = multiple ? "multiple" : "single";

        return (
          <div
            key={option.value}
            className={`chip ${selectionClass} ${modeClass} ${option.disabled ? "disabled" : ""}`}
            onClick={() =>
              !option.disabled && (isUsingReactHookForm ? handleChangeHookForm(option.value) : handleSelect(option.value))
            }
          >
            {!multiple && option.icon && option.icon}
            {multiple && isSelected && <Check />}
            <div>{option.label}</div>
          </div>
        );
      })}
    </div>
  );
};

export default ChoiceChip;
